import { useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getAllCompanyModuleConfigs,
  updateCompanyModuleConfig,
  updateCompanyPageConfig,
  getCompanyPageConfig,
  CompanyModuleConfigView,
  ModuleConfigInput,
  PageConfigInput,
  PageConfigView,
} from '../../../api/Navigation/moduleConfig';
import {
  navigationModules,
  profileTabs,
  PROFILE_TAB_MODULE_ID,
  mobileModules,
} from '../../../components/layout/navigationModules';
import { ChevronDown, Lock, Smartphone, Monitor, LayoutGrid } from 'lucide-react';
import { PageLayout } from '../../../components/ui/PageLayout';
import StickyActionBar from '../../../components/ui/StickyActionBar';
import { Switch } from '../../../components/ui/Switch';

interface ToastState {
  show: boolean;
  message: string;
  type: 'success' | 'error';
}

/**
 * A single toggle row inside an expanded module/section panel.
 * Reused for web pages, sub-module pages, mobile pages, and profile tabs.
 */
interface ConfigRowProps {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onToggle: (value: boolean) => void;
  badge?: ReactNode;
}

function ConfigRow({ label, checked, disabled = false, onToggle, badge }: ConfigRowProps) {
  return (
    <div className="flex items-center justify-between gap-3 px-6 py-3 transition-colors hover:bg-card">
      <div className="flex min-w-0 items-center gap-2">
        <span className={`truncate text-sm ${disabled ? 'text-muted-foreground' : 'text-foreground'}`}>
          {label}
        </span>
        {badge}
      </div>
      <Switch checked={checked} disabled={disabled} onCheckedChange={onToggle} />
    </div>
  );
}

/**
 * Module Configuration page for Operators.
 * Accordion layout with per-module and per-page toggles across the web app,
 * mobile app, and profile tabs. Supports dirty tracking and reset/save via the
 * sticky action bar.
 */
export default function ModuleConfig() {
  const { t } = useTranslation();

  // Data state
  const [companies, setCompanies] = useState<CompanyModuleConfigView[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [moduleStates, setModuleStates] = useState<Record<string, boolean>>({});
  const [pageStates, setPageStates] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // UI state
  const [expandedModules, setExpandedModules] = useState<string[]>([]);
  const [profileTabsExpanded, setProfileTabsExpanded] = useState(false);
  const [expandedMobileModules, setExpandedMobileModules] = useState<string[]>([]);
  const [toast, setToast] = useState<ToastState>({ show: false, message: '', type: 'success' });

  // Initial state snapshots for dirty tracking
  const [initialModuleStates, setInitialModuleStates] = useState<string>('{}');
  const [initialPageStates, setInitialPageStates] = useState<string>('{}');

  // Dirty state
  const isDirty =
    JSON.stringify(moduleStates) !== initialModuleStates ||
    JSON.stringify(pageStates) !== initialPageStates;

  // Toast auto-dismiss
  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  // Load companies on mount
  useEffect(() => {
    const loadCompanies = async () => {
      setLoading(true);
      const result = await getAllCompanyModuleConfigs();
      setCompanies(result);
      setLoading(false);
    };
    loadCompanies();
  }, []);

  // Build initial states from company data + page config
  const buildStates = useCallback((
    selectedCompany: CompanyModuleConfigView,
    pageConfig: PageConfigView[]
  ) => {
    // Build module states
    const mStates: Record<string, boolean> = {};
    navigationModules.forEach(module => {
      mStates[module.id] = true; // default enabled
    });
    // Seed mobile module states (default all enabled)
    mobileModules.forEach(mobileModule => {
      mStates[mobileModule.id] = true;
    });
    if (selectedCompany.modules && selectedCompany.modules.length > 0) {
      selectedCompany.modules.forEach(mc => {
        mStates[mc.moduleId] = mc.isEnabled;
      });
    }
    mStates['home'] = true; // always enabled

    // Build page states
    const pStates: Record<string, boolean> = {};
    navigationModules.forEach(module => {
      module.pages.forEach(page => {
        pStates[`${module.id}:${page.path}`] = true; // default all enabled
      });
      // Sub-module pages are keyed by the sub-module id (the runtime nav filter
      // matches on pagePath alone, so the moduleId is purely organizational).
      module.subModules?.forEach(sub => {
        sub.pages.forEach(page => {
          pStates[`${sub.id}:${page.path}`] = true;
        });
      });
    });
    // Seed profile tab keys (default all enabled)
    profileTabs.forEach(tab => {
      pStates[`${PROFILE_TAB_MODULE_ID}:${tab.id}`] = true;
    });
    // Seed all mobile module page keys (default all enabled)
    mobileModules.forEach(mobileModule => {
      mobileModule.pages.forEach(page => {
        pStates[`${mobileModule.id}:${page.id}`] = true;
      });
    });
    // Override from API
    pageConfig.forEach(pc => {
      const key = `${pc.moduleId}:${pc.pagePath}`;
      if (key in pStates) {
        pStates[key] = pc.isEnabled;
      }
    });

    return { mStates, pStates };
  }, []);

  // Populate states when company is selected
  useEffect(() => {
    if (selectedCompanyId === null) {
      setModuleStates({});
      setPageStates({});
      setInitialModuleStates('{}');
      setInitialPageStates('{}');
      setExpandedModules([]);
      return;
    }

    const selectedCompany = companies.find(c => c.companyId === selectedCompanyId);
    if (!selectedCompany) {
      setModuleStates({});
      setPageStates({});
      return;
    }

    const loadPageConfig = async () => {
      const pageConfig = await getCompanyPageConfig(selectedCompanyId);
      const { mStates, pStates } = buildStates(selectedCompany, pageConfig);

      setModuleStates(mStates);
      setPageStates(pStates);
      setInitialModuleStates(JSON.stringify(mStates));
      setInitialPageStates(JSON.stringify(pStates));
    };

    loadPageConfig();
  }, [selectedCompanyId, companies, buildStates]);

  const handleCompanySelect = (companyId: number | null) => {
    setSelectedCompanyId(companyId);
    setExpandedModules([]);
    setExpandedMobileModules([]);
    setProfileTabsExpanded(false);
  };

  const handleModuleToggle = (moduleId: string, value: boolean) => {
    if (moduleId === 'home') return;
    setModuleStates(prev => ({ ...prev, [moduleId]: value }));
  };

  const handlePageToggle = (moduleId: string, pagePath: string, value: boolean) => {
    setPageStates(prev => ({ ...prev, [`${moduleId}:${pagePath}`]: value }));
  };

  const toggleExpand = (moduleId: string) => {
    if (moduleId === 'home') return; // Home is non-expandable
    setExpandedModules(prev =>
      prev.includes(moduleId)
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  const toggleMobileModuleExpanded = (moduleId: string) => {
    setExpandedMobileModules(prev =>
      prev.includes(moduleId)
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  const handleReset = () => {
    setModuleStates(JSON.parse(initialModuleStates) as Record<string, boolean>);
    setPageStates(JSON.parse(initialPageStates) as Record<string, boolean>);
  };

  const handleSave = async () => {
    if (selectedCompanyId === null || !isDirty) return;

    setSaving(true);

    // Build module config input
    const modules: ModuleConfigInput[] = Object.entries(moduleStates).map(([moduleId, isEnabled]) => ({
      moduleId,
      isEnabled
    }));

    // Build page config input
    const pages: PageConfigInput[] = Object.entries(pageStates).map(([key, isEnabled]) => {
      const [moduleId, ...pathParts] = key.split(':');
      const pagePath = pathParts.join(':'); // rejoin in case path had colons
      return { moduleId, pagePath, isEnabled };
    });

    // Save both in parallel
    const [moduleSuccess, pageSuccess] = await Promise.all([
      updateCompanyModuleConfig(selectedCompanyId, modules),
      updateCompanyPageConfig(selectedCompanyId, pages),
    ]);

    if (moduleSuccess && pageSuccess) {
      // Refresh companies list
      const result = await getAllCompanyModuleConfigs();
      setCompanies(result);
      // Update initial snapshots to reset dirty state
      setInitialModuleStates(JSON.stringify(moduleStates));
      setInitialPageStates(JSON.stringify(pageStates));
      setToast({ show: true, message: t('Configuration saved successfully'), type: 'success' });
    } else {
      setToast({ show: true, message: t('Failed to save configuration'), type: 'error' });
    }

    setSaving(false);
  };

  // Shared badges (depend on `t`, so built here rather than at module scope)
  const lockBadge = (
    <span className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
      <Lock className="h-3 w-3" />
      {t('Always enabled')}
    </span>
  );
  const comingSoonBadge = (
    <span className="inline-flex shrink-0 items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
      {t('Coming Soon')}
    </span>
  );

  if (loading) {
    return (
      <PageLayout
        title={t('Module Configuration')}
        icon={LayoutGrid}
        accentColor="slate"
        maxWidth="5xl"
      >
        <div className="animate-pulse">
          <div className="mb-6 h-10 w-1/2 rounded bg-muted"></div>
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-16 rounded-lg bg-muted"></div>
            ))}
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={t('Module Configuration')}
      icon={LayoutGrid}
      accentColor="slate"
      maxWidth="5xl"
    >
      {/* Toast notification */}
      {toast.show && (
        <div
          className={`fixed inset-x-0 bottom-8 z-50 mx-auto w-fit rounded-lg px-6 py-3 shadow-lg ${
            toast.type === 'success'
              ? 'bg-success text-success-foreground'
              : 'bg-destructive text-destructive-foreground'
          }`}
        >
          {toast.message}
        </div>
      )}

      {/* Company selector */}
      <div className="mb-6">
        <label className="mb-2 block text-sm font-medium text-foreground">
          {t('Select Company')}
        </label>
        <select
          className="w-full max-w-md rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          value={selectedCompanyId ?? ''}
          onChange={(e) => handleCompanySelect(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">{t('Select a company...')}</option>
          {companies.map(c => (
            <option key={c.companyId} value={c.companyId}>{c.companyName}</option>
          ))}
        </select>
      </div>

      {/* Empty state when no companies */}
      {companies.length === 0 && (
        <div className="py-12 text-center text-muted-foreground">
          <p>{t('No companies found. Only Operators can access this page.')}</p>
        </div>
      )}

      {/* Web App section */}
      {selectedCompanyId !== null && (
        <div className="mb-6">
          <div className="mb-3 flex items-center gap-2">
            <Monitor className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold text-foreground">{t('Web App')}</h2>
          </div>
          <div className="space-y-3">
            {navigationModules.map(module => {
              const isHome = module.id === 'home';
              const isEnabled = moduleStates[module.id] ?? true;
              const isExpanded = expandedModules.includes(module.id);
              const moduleDisabled = !isEnabled;
              const totalPages =
                module.pages.length +
                (module.subModules?.reduce((n, s) => n + s.pages.length, 0) ?? 0);

              return (
                <div
                  key={module.id}
                  className="overflow-hidden rounded-lg border border-border bg-card shadow"
                >
                  {/* Card Header */}
                  <div
                    role={isHome ? undefined : 'button'}
                    tabIndex={isHome ? undefined : 0}
                    aria-expanded={isHome ? undefined : isExpanded}
                    className={`flex select-none items-center justify-between gap-3 px-5 py-4 ${
                      isHome ? 'bg-muted' : 'cursor-pointer hover:bg-muted'
                    }`}
                    onClick={() => toggleExpand(module.id)}
                    onKeyDown={(e) => {
                      if (!isHome && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        toggleExpand(module.id);
                      }
                    }}
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br ${module.color}`}>
                        <span className="text-sm font-semibold text-white">
                          {module.title.charAt(0)}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="truncate font-medium text-foreground">{t(module.title)}</p>
                        <p className="text-sm text-muted-foreground">
                          {totalPages} {t(totalPages === 1 ? 'page' : 'pages')}
                        </p>
                      </div>
                      {isHome && <span className="ms-2">{lockBadge}</span>}
                    </div>

                    <div className="flex shrink-0 items-center gap-3">
                      {/* Module toggle (stop propagation so it doesn't expand the card) */}
                      <span
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <Switch
                          checked={isEnabled}
                          disabled={isHome}
                          onCheckedChange={(v) => handleModuleToggle(module.id, v)}
                        />
                      </span>

                      {/* Expand chevron */}
                      {!isHome && (
                        <ChevronDown
                          className={`h-5 w-5 text-muted-foreground transition-transform duration-200 ${
                            isExpanded ? 'rotate-180' : ''
                          }`}
                        />
                      )}
                    </div>
                  </div>

                  {/* Expanded Page List */}
                  {isExpanded && !isHome && (
                    <div className="border-t border-border bg-muted">
                      {module.pages.map(page => (
                        <ConfigRow
                          key={`${module.id}:${page.path}`}
                          label={t(page.title)}
                          checked={pageStates[`${module.id}:${page.path}`] ?? true}
                          disabled={moduleDisabled}
                          onToggle={(v) => handlePageToggle(module.id, page.path, v)}
                          badge={page.comingSoon ? comingSoonBadge : undefined}
                        />
                      ))}

                      {module.subModules?.map(sub => (
                        <div key={sub.id}>
                          <div className="px-6 pb-1 pt-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                            {t(sub.title)}
                          </div>
                          {sub.pages.map(page => (
                            <ConfigRow
                              key={`${sub.id}:${page.path}`}
                              label={t(page.title)}
                              checked={pageStates[`${sub.id}:${page.path}`] ?? true}
                              disabled={moduleDisabled}
                              onToggle={(v) => handlePageToggle(sub.id, page.path, v)}
                              badge={page.comingSoon ? comingSoonBadge : undefined}
                            />
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Mobile App section */}
      {selectedCompanyId !== null && (
        <div className="mb-6">
          <div className="mb-3 flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold text-foreground">{t('Mobile App')}</h2>
          </div>
          <div className="space-y-3">
            {mobileModules.map(mobileModule => {
              const isEnabled = moduleStates[mobileModule.id] ?? true;
              const isExpanded = expandedMobileModules.includes(mobileModule.id);
              const moduleDisabled = !isEnabled;
              const pageCount = mobileModule.pages.length;

              return (
                <div
                  key={mobileModule.id}
                  className="overflow-hidden rounded-lg border border-border bg-card shadow"
                >
                  {/* Card Header */}
                  <div
                    role="button"
                    tabIndex={0}
                    aria-expanded={isExpanded}
                    className="flex cursor-pointer select-none items-center justify-between gap-3 px-5 py-4 hover:bg-muted"
                    onClick={() => toggleMobileModuleExpanded(mobileModule.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleMobileModuleExpanded(mobileModule.id);
                      }
                    }}
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br ${mobileModule.color}`}>
                        <span className="text-sm font-semibold text-white">
                          {mobileModule.title.charAt(0)}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="truncate font-medium text-foreground">{t(mobileModule.title)}</p>
                        <p className="text-sm text-muted-foreground">
                          {pageCount} {t(pageCount === 1 ? 'page' : 'pages')}
                        </p>
                      </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-3">
                      <span
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <Switch
                          checked={isEnabled}
                          onCheckedChange={(v) => handleModuleToggle(mobileModule.id, v)}
                        />
                      </span>
                      <ChevronDown
                        className={`h-5 w-5 text-muted-foreground transition-transform duration-200 ${
                          isExpanded ? 'rotate-180' : ''
                        }`}
                      />
                    </div>
                  </div>

                  {/* Expanded Page List */}
                  {isExpanded && (
                    <div className="border-t border-border bg-muted">
                      {mobileModule.pages.map(page => {
                        const isLocked = page.alwaysEnabled === true;
                        return (
                          <ConfigRow
                            key={page.id}
                            label={t(page.title)}
                            checked={isLocked ? true : (pageStates[`${mobileModule.id}:${page.id}`] ?? true)}
                            disabled={moduleDisabled || isLocked}
                            onToggle={(v) => handlePageToggle(mobileModule.id, page.id, v)}
                            badge={isLocked ? lockBadge : undefined}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Profile Page Tabs section */}
      {selectedCompanyId !== null && (
        <div className="mb-6">
          <div className="overflow-hidden rounded-lg border border-border bg-card shadow">
            {/* Header */}
            <div
              role="button"
              tabIndex={0}
              aria-expanded={profileTabsExpanded}
              className="flex cursor-pointer select-none items-center justify-between gap-3 px-5 py-4 hover:bg-muted"
              onClick={() => setProfileTabsExpanded(prev => !prev)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setProfileTabsExpanded(prev => !prev);
                }
              }}
            >
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-blue-600">
                  <span className="text-sm font-semibold text-white">P</span>
                </div>
                <div className="min-w-0">
                  <p className="truncate font-medium text-foreground">{t('Profile Page Tabs')}</p>
                  <p className="text-sm text-muted-foreground">
                    {profileTabs.length} {t('tabs')}
                  </p>
                </div>
              </div>
              <ChevronDown
                className={`h-5 w-5 text-muted-foreground transition-transform duration-200 ${
                  profileTabsExpanded ? 'rotate-180' : ''
                }`}
              />
            </div>

            {/* Expanded Tab List */}
            {profileTabsExpanded && (
              <div className="border-t border-border bg-muted">
                {profileTabs.map(tab => {
                  const isLocked = tab.alwaysEnabled === true;
                  return (
                    <ConfigRow
                      key={tab.id}
                      label={t(tab.title)}
                      checked={isLocked ? true : (pageStates[`${PROFILE_TAB_MODULE_ID}:${tab.id}`] ?? true)}
                      disabled={isLocked}
                      onToggle={(v) => handlePageToggle(PROFILE_TAB_MODULE_ID, tab.id, v)}
                      badge={isLocked ? lockBadge : undefined}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sticky save / reset bar */}
      {selectedCompanyId !== null && (
        <StickyActionBar
          hasUnsavedChanges={isDirty}
          isSaving={saving}
          onReset={handleReset}
          onSave={handleSave}
          t={t}
        />
      )}
    </PageLayout>
  );
}
